import { useCallback, useEffect, useMemo } from "react";

import { useUserLocalProjectsQuery } from "./queries/useUserLocalProjectsQuery";
import { useRollupsQuery } from "./queries/useRollupsQuery";
import { useQueryString } from "../../core/hooks/useQueryString";

import {
  IProject,
  IRollupProject,
  IRollups,
  RollupsType
} from "../types/entities";

const allProjects = {
  id: "all",
  name: "All Projects",
  subdomain: ""
};

export type SelectedProject = Partial<IProject> & {
  subdomain: string;
};

export const useSelectedProject = (): {
  isSingleProject: boolean;
  selectedProject?: SelectedProject;
  setSelectedProject: (selectedProjectId: string) => void;
  rollupsType: RollupsType;
  rollups: IRollups;
  rollupProjects: IRollupProject[];
} => {
  const { data: projects } = useUserLocalProjectsQuery();
  const { data: rollupsData } = useRollupsQuery();

  const { urlQueries, addToQS } = useQueryString();

  const { project: activeId } = urlQueries;

  const rollupsType = useMemo((): RollupsType => {
    if (activeId === "all") return "project";

    const isCollection =
      rollupsData &&
      rollupsData.collections.some(collection => collection.id === activeId);

    if (isCollection) return "collection";

    const isGroup =
      rollupsData && rollupsData.groups.some(group => group.id === activeId);

    if (isGroup) return "group";

    return "project";
  }, [activeId, rollupsData]);

  const selectedProject = useMemo((): SelectedProject => {
    if (activeId === "all") {
      return allProjects;
    }

    if (rollupsData && rollupsType === "collection") {
      return (
        rollupsData.collections.find(
          collection => collection.id === activeId
        ) || allProjects
      );
    }

    if (rollupsData && rollupsType === "group") {
      return (
        rollupsData.groups.find(group => group.id === activeId) || allProjects
      );
    }

    const defaultProject = {
      id: activeId as string,
      name: "",
      subdomain: ""
    };

    const project = projects
      ? projects.find(project => project.id === activeId) || defaultProject
      : defaultProject;

    return project;
  }, [projects, activeId, rollupsData, rollupsType]);

  const setSelectedProject = useCallback(
    (selectedProjectId: string) => {
      addToQS({
        queryObject: { project: selectedProjectId },
        isReplace: true
      });
    },
    [addToQS]
  );

  useEffect(() => {
    if (rollupsData) {
      let isProjectIdValid = true;

      if (rollupsType === "collection") {
        const collection = rollupsData.collections.find(
          collection => collection.id === activeId
        );

        const hasProjects = rollupsData.projects.some(({ group_id }) => {
          const projectGroup = rollupsData.groups.find(
            group => group.id === group_id
          );

          return projectGroup?.collection_id === collection?.id;
        });

        isProjectIdValid = Boolean(collection) && hasProjects;
      }

      if (rollupsType === "group") {
        const group = rollupsData.groups.find(group => group.id === activeId);

        const hasProjects = rollupsData.projects.some(
          ({ group_id }) => group_id === group?.id
        );

        isProjectIdValid = Boolean(group) && hasProjects;
      }

      if (rollupsType === "project") {
        isProjectIdValid =
          activeId === "all" ||
          rollupsData.projects.some(project => project.id === activeId);
      }

      if (!isProjectIdValid) {
        setSelectedProject("all");
      }
    }
  }, [activeId, rollupsData, rollupsType, setSelectedProject]);

  const rollupProjects = useMemo(() => {
    if (!rollupsData) return [];

    switch (rollupsType) {
      case "collection": {
        const groups = rollupsData.groups.filter(
          group => group.collection_id === activeId
        );
        return groups.flatMap(group =>
          rollupsData.projects.filter(project => project.group_id === group.id)
        );
      }

      case "group": {
        return rollupsData.projects.filter(
          project => project.group_id === activeId
        );
      }

      case "project": {
        return rollupsData.projects.filter(project => project.id === activeId);
      }

      default: {
        return [];
      }
    }
  }, [activeId, rollupsData, rollupsType]);

  const rollups = useMemo(() => {
    return rollupsData || { collections: [], groups: [], projects: [] };
  }, [rollupsData]);

  const isSingleProject =
    rollupsType === "project" && selectedProject?.id !== "all";

  return {
    isSingleProject,
    selectedProject,
    setSelectedProject,
    rollupsType,
    rollups,
    rollupProjects
  };
};
